import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Http, Response } from "@angular/http";
import { ParentService } from "../A2-utils/ParentClass/ParentService";
import { constant } from "../../app.constants";
import { DeleteQuizResponse, IQuiz, QuizAnswerClass, QuizClass, QuizQuestionClass } from "./quiz.class";
import { QuizAnswerFormAction, QuizQuestionFormAction } from "./quiz-actions.interface";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable()
export class QuizService extends ParentService {
  smartTableData = [];
  comboPayorsData = [];

  private quizData = new BehaviorSubject<QuizClass>(new QuizClass());
  quizData$ = this.quizData.asObservable();

  private modalStatus = new BehaviorSubject<boolean>(false);
  modalStatus$ = this.modalStatus.asObservable();
  private refreshQuizGrid = new BehaviorSubject<boolean>(false);
  refreshQuizGrid$ = this.refreshQuizGrid.asObservable();
  private questionFormAction = new BehaviorSubject<QuizQuestionFormAction>(null);
  questionFormAction$ = this.questionFormAction.asObservable();
  private answerFormAction = new BehaviorSubject<QuizAnswerFormAction>(null);
  answerFormAction$ = this.answerFormAction.asObservable();
  private questionFormSaved = new BehaviorSubject<boolean>(false);
  questionFormSaved$ = this.questionFormSaved.asObservable();
  private answerFormSaved = new BehaviorSubject<boolean>(false);
  answerFormSaved$ = this.answerFormSaved.asObservable();

  constructor(private _http: Http, private route: Router) {
    super();
  }

  /**
   * @namespace BehaviorsSubjects
   */
  setQuizData(data: QuizClass) {
    this.quizData.next(data);
  }

  addQuestionToQuiz(question: QuizQuestionClass) {
    const quiz = this.getCurrentQuiz();
    if (!quiz) {
      return;
    }
    if (!quiz.quizquestion) {
      quiz.quizquestion = [];
    }
    quiz.quizquestion.push(question);
    this.quizData.next(quiz);
  }

  updateQuestionDescription(questionIndex: number, description: string) {
    const question = this.getQuestion(questionIndex);
    if (!question) {
      return;
    }
    question.description = description;
    this.quizData.next(this.getCurrentQuiz());
  }

  addAnswerToQuestion(questionIndex: number, answer: QuizAnswerClass) {
    const question = this.getQuestion(questionIndex);
    if (!question) {
      return;
    }
    if (!question.quizanswer) {
      question.quizanswer = [];
    }
    question.quizanswer.push(answer);
    this.quizData.next(this.getCurrentQuiz());
  }

  updateAnswerInQuestion(
    questionIndex: number,
    answerIndex: number,
    description: string,
    isCorrect: boolean
  ) {
    const answer = this.getAnswer(questionIndex, answerIndex);
    if (!answer) {
      return;
    }
    answer.description = description;
    answer.iscorrect = isCorrect;
    this.quizData.next(this.getCurrentQuiz());
  }

  private getCurrentQuiz(): QuizClass {
    return this.quizData.getValue();
  }

  private getQuestion(questionIndex: number): QuizQuestionClass {
    const quiz = this.getCurrentQuiz();
    if (!quiz || !quiz.quizquestion || !this.isValidIndex(questionIndex, quiz.quizquestion.length)) {
      return null;
    }
    return quiz.quizquestion[questionIndex];
  }

  private getAnswer(questionIndex: number, answerIndex: number): QuizAnswerClass {
    const question = this.getQuestion(questionIndex);
    if (!question || !question.quizanswer || !this.isValidIndex(answerIndex, question.quizanswer.length)) {
      return null;
    }
    return question.quizanswer[answerIndex];
  }

  private isValidIndex(index: number, length: number): boolean {
    return index >= 0 && index < length;
  }

  setModal(status: boolean) {
    this.modalStatus.next(status);
  }

  setRefreshQuizGrid(refresh: boolean) {
    this.refreshQuizGrid.next(refresh);
  }

  setQuestionFormAction(action: QuizQuestionFormAction) {
    this.questionFormAction.next(action);
  }

  setAnswerFormAction(action: QuizAnswerFormAction) {
    this.answerFormAction.next(action);
  }

  setQuestionFormSaved(saved: boolean) {
    this.questionFormSaved.next(saved);
  }

  setAnswerFormSaved(saved: boolean) {
    this.answerFormSaved.next(saved);
  }

  /**
   * @name getVehicles
   */
  getQuiz(showInactive: boolean = false) {
    return this._http
      .get(`${constant.URL_BASE_QUIZ}?showInactive=${showInactive}`, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name getVehicleById
   * @param {number} id
   */
  getQuizById(id: number) {
    return this._http
      .get(`${constant.URL_BASE_QUIZ}/${id}`, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name postQuiz
   * @param QuizClass
   */
  postQuiz(form: IQuiz) {
    return this._http
      .post(constant.URL_BASE_QUIZ, form, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name putQuiz
   * @param QuizClass
  */
  putQuiz(form: IQuiz) {
    return this._http
      .put(constant.URL_BASE_QUIZ, form, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  deleteQuiz(quizId: number): Observable<DeleteQuizResponse> {
    return this._http
      .delete(`${constant.URL_BASE_QUIZ}/${quizId}`, this.getHeader())
      .map((res: Response) => <DeleteQuizResponse>res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name postQuizQuestion
   * @param QuizQuestionClass
  */
  postQuizQuestion(form: QuizQuestionClass) {
    return this._http
      .post(constant.URL_BASE_QUIZ_QUESTION, form, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name putQuizQuestion
   * @param QuizQuestionClass
  */
  putQuizQuestion(form: QuizQuestionClass) {
    return this._http
      .put(constant.URL_BASE_QUIZ_QUESTION, form, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name postQuizAnswer
   * @param QuizAnswerClass
  */
  postQuizAnswer(form: QuizAnswerClass) {
    return this._http
      .post(constant.URL_BASE_QUIZ_ANSWER, form, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

  /**
   * @name putQuizAnswer
   * @param QuizAnswerClass
  */
  putQuizAnswer(form: QuizAnswerClass) {
    return this._http
      .put(constant.URL_BASE_QUIZ_ANSWER, form, this.getHeader())
      .map((res) => res.json())
      .catch(error => Observable.throw(error.json()));
  }

}



// WEBPACK FOOTER //
// ./src/app/pages/quiz/quiz.service.ts